import * as React from 'react';
import { FaCheck } from '@meronex/icons/fa';
import { Box, Button, ButtonProps, Input, InputProps } from '@apia/theme';
import { useUpdateEffect } from '@react-hookz/web';
import { TModify } from '../../../types';
import { customEvents } from '../../../utils/dom';
import usePortal from 'react-cool-portal';

export type TCheckbox = TModify<
  ButtonProps,
  {
    background?: string;
    checked?: boolean;
    color?: string;
    defaultChecked?: boolean;
    indeterminate?: boolean;
    native?: boolean;
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
    readOnly?: boolean;
    required?: boolean;
    type?: string;
    inputProps?: InputProps;
    portalId?: string;
  }
>;

const Checkbox = React.forwardRef<HTMLButtonElement, TCheckbox>(
  (
    {
      onChange,
      required,
      disabled,
      readOnly,
      name,
      native,
      indeterminate,
      inputProps,
      portalId,
      ...props
    },
    ref,
  ) => {
    const [isChecked, setIsChecked] = React.useState(
      props.defaultChecked ?? props.checked ?? false,
    );

    const checkbox = React.useRef<HTMLInputElement>(null);

    useUpdateEffect(
      () => setIsChecked(props.checked ?? false),
      [props.checked],
    );

    const sx = React.useMemo(
      () => ({
        bg: props.background ?? undefined,
        color: props.color ?? undefined,
        '&[aria-checked="false"]': {
          bg: props.background ?? undefined,
          color: 'transparent',
        },
      }),
      [props.background, props.color],
    );

    React.useEffect(() => {
      if (checkbox.current && indeterminate !== undefined)
        checkbox.current.indeterminate = indeterminate;
    }, [indeterminate]);

    const nativeSx = React.useMemo(() => ({ variant: 'forms.checkbox' }), []);

    const [boxRef, setBoxRef] = React.useState<HTMLElement | null>(null);

    React.useEffect(() => {
      const handleFocus = () => {
        boxRef
          ?.querySelector<HTMLElement>('[type="button"][role="checkbox"]')
          ?.focus?.();
      };

      boxRef?.addEventListener(customEvents.focus, handleFocus);

      return () => boxRef?.removeEventListener(customEvents.focus, handleFocus);
    }, [boxRef]);

    const { Portal, show } = usePortal({
      containerId: portalId ?? 'AppModals',
      defaultShow: true,
      autoRemoveContainer: false,
      escToHide: false,
      clickOutsideToHide: false,
    });
    show();

    return (
      <Box as="span" className="checkbox" ref={setBoxRef}>
        {native ? (
          <input
            type="checkbox"
            onClick={props.onClick as unknown as React.MouseEventHandler}
            aria-checked={isChecked}
            sx={nativeSx}
            ref={checkbox}
            disabled={disabled}
            name={name}
            checked={isChecked}
            onChange={(ev) => {
              if (props.checked === undefined) setIsChecked(ev.target.checked);
              if (onChange) onChange(ev);
            }}
            required={required}
            {...inputProps}
            {...(props as unknown as InputProps)}
            className={` ${props.className ?? ''} nativeCheckbox `}
          />
        ) : (
          <>
            <Portal>
              <Input
                type="checkbox"
                sx={{ display: 'none' }}
                ref={checkbox}
                onClick={(ev) => {
                  ev.stopPropagation();
                }}
                name={name}
                checked={isChecked}
                onChange={(ev) => {
                  if (props.checked === undefined)
                    setIsChecked(ev.target.checked);
                  if (onChange) onChange(ev);
                }}
                required={required}
                disabled={disabled}
                {...inputProps}
              />
            </Portal>
            <Button
              {...props}
              onClick={(ev) => {
                if (readOnly) {
                  return;
                }
                if ((ev.target as HTMLInputElement).type === 'button') {
                  if (props.onClick) props.onClick(ev);
                  checkbox.current?.click();
                }
              }}
              type="button"
              {...window.getVariant('forms.customCheckbox')}
              role="checkbox"
              aria-checked={isChecked}
              sx={sx}
              ref={ref}
              disabled={disabled}
              className={`${props.className ?? ''} ${
                readOnly ? 'readonly' : ''
              }`}
            >
              {isChecked ? (
                <FaCheck sx={{ pointerEvents: 'none' }} size="32px" />
              ) : null}
            </Button>
          </>
        )}
      </Box>
    );
  },
);

export default Checkbox;
